import { useState } from "react";
import { ArrowRight, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import PhoneInput from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import { PriceSummary } from "@/modules/eventRegistration/features/EventRegistration/components/PriceSummary/PriceSummary";
import { useRegistration } from "@/modules/eventRegistration/features/EventRegistration/context/RegistrationContext";
import { useNotification } from "@/context/NotificationContext";
import AddAttendees from "@/modules/eventRegistration/features/EventRegistration/components/AddAttendees/AddAttendees";
import {
  computeWaitlistedCount,
  computeConfirmedCount,
  recomputeAttendeeWaitlistFlags,
} from "@/modules/eventRegistration/features/EventRegistration/utils/attendeeWaitlist";
import { normalizeEmail } from "@/modules/eventRegistration/features/EventRegistration/utils/normalizeEmail";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ReviewStep() {
  const { showNotification } = useNotification();
  const {
    contactName,
    contactEmail,
    contactPhone,
    attendees,
    groupNotes,
    eventInfo,
    updateContact,
    setAttendees,
    nextStep,
  } = useRegistration();

  const eventPrice = eventInfo?.eventPrice ?? 0;
  const availableSeats = eventInfo?.availableSeats ?? 0;
  const isCoveredByTotalAccess = eventInfo?.isCoveredByTotalAccess ?? false;
  const isCoveredByStage = eventInfo?.isCoveredByStage ?? false;

  const confirmedCount = computeConfirmedCount(
    attendees.length,
    availableSeats,
  );
  const waitlistedCount = computeWaitlistedCount(
    attendees.length,
    availableSeats,
  );

  const [duplicateEmailError, setDuplicateEmailError] = useState<string | null>(
    null,
  );

  // The logged-in user's own auto-added seat. Rendered as its own card above
  // the team list so "you're registered by default" can't be missed.
  const selfIndex = attendees.findIndex((a) => a.isSelf);
  const selfAttendee = selfIndex >= 0 ? attendees[selfIndex] : null;
  // Only offer the removed-self banner when a seat was auto-added to begin
  // with (defaultAttendee is null when the user is already enrolled).
  const selfSeatWasOffered = Boolean(eventInfo?.defaultAttendee);

  function handleAddSelfBack() {
    const defaultAttendee = eventInfo?.defaultAttendee;
    if (!defaultAttendee) return;

    // If the user already re-added themselves by hand (that row carries no
    // isSelf flag), promote it to the self seat instead of duplicating the
    // registration on the same email.
    const selfEmail = defaultAttendee.email
      ? normalizeEmail(defaultAttendee.email)
      : null;
    const existingIndex = selfEmail
      ? attendees.findIndex(
          (a) => a.email && normalizeEmail(a.email) === selfEmail,
        )
      : -1;
    if (existingIndex >= 0) {
      const existing = attendees[existingIndex];
      setAttendees(
        recomputeAttendeeWaitlistFlags(
          [
            { ...existing, isSelf: true },
            ...attendees.filter((_, i) => i !== existingIndex),
          ],
          availableSeats,
        ),
      );
      return;
    }

    setAttendees(
      recomputeAttendeeWaitlistFlags(
        [
          {
            firstName: defaultAttendee.firstName,
            lastName: defaultAttendee.lastName,
            email: defaultAttendee.email,
            isSelected: true,
            isWaitlist: false,
            specialRequests: defaultAttendee.specialRequests || undefined,
            isSelf: true,
          },
          ...attendees,
        ],
        availableSeats,
      ),
    );
  }

  // Add a single attendee straight into context — no edit/save step. Adds
  // are deduped on email and the whole list's waitlist flags are recomputed
  // so the badges + price summary stay in sync immediately.
  function handleAddAttendee(attendee: {
    firstName: string;
    lastName: string;
    email?: string;
  }) {
    if (attendee.email) {
      const normalizedEmail = normalizeEmail(attendee.email);
      const isDuplicate = attendees.some(
        (a) => a.email && normalizeEmail(a.email) === normalizedEmail,
      );
      if (isDuplicate) {
        setDuplicateEmailError(
          `An attendee with email ${attendee.email} has already been added.`,
        );
        return;
      }
      // Also reject people who already hold a seat in this session from a
      // previous checkout — the same rule the server enforces at
      // completion, surfaced here at the point of adding (#9725).
      const alreadyRegistered = (eventInfo?.existingAttendees ?? []).some(
        (a) => a.email && normalizeEmail(a.email) === normalizedEmail,
      );
      if (alreadyRegistered) {
        setDuplicateEmailError(
          `${attendee.email} is already registered for this session.`,
        );
        return;
      }
    }
    setDuplicateEmailError(null);

    // Adding yourself from the directory IS taking your seat back: flag the
    // row isSelf and keep it at index 0 (the self seat stays confirmed
    // first), so the "you're not attending" banner clears instead of
    // lingering next to a row that is you.
    const isSelfReAdd = Boolean(
      eventInfo?.defaultAttendee?.email &&
        attendee.email &&
        normalizeEmail(attendee.email) ===
          normalizeEmail(eventInfo.defaultAttendee.email),
    );
    const newAttendee = {
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      email: attendee.email,
      isSelected: true,
      isWaitlist: false,
      specialRequests: undefined,
      isSelf: isSelfReAdd || undefined,
    };
    setAttendees(
      recomputeAttendeeWaitlistFlags(
        isSelfReAdd ? [newAttendee, ...attendees] : [...attendees, newAttendee],
        availableSeats,
      ),
    );
  }

  function handleRemoveAttendee(index: number) {
    setAttendees(
      recomputeAttendeeWaitlistFlags(
        attendees.filter((_, i) => i !== index),
        availableSeats,
      ),
    );
  }

  function handleSpecialRequestChange(index: number, value: string) {
    setAttendees(
      attendees.map((a, i) =>
        i === index ? { ...a, specialRequests: value } : a,
      ),
    );
  }

  function handleContinue() {
    if (!contactName || !contactEmail || !contactPhone) {
      showNotification(
        "Missing Contact Info",
        "Please fill in contact name, email, and phone.",
        "error",
      );
      return;
    }

    if (!EMAIL_PATTERN.test(contactEmail)) {
      showNotification(
        "Invalid Email",
        "Please enter a valid contact email address.",
        "error",
      );
      return;
    }

    if (attendees.length === 0) {
      showNotification(
        "No Attendees",
        "Please add at least one attendee.",
        "error",
      );
      return;
    }

    // Keep waitlist flags authoritative before advancing — buildEventCart
    // reads a.isWaitlist to decide who needs a paid seat.
    setAttendees(recomputeAttendeeWaitlistFlags(attendees, availableSeats));
    nextStep();
  }

  return (
    <section className="space-y-6">
      <h2 className="text-lg font-normal text-gray-900 dark:text-white">
        Register your team
      </h2>

      {/* Contact card — always editable, no save step. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact Person</CardTitle>
          <CardDescription>
            This person will receive all event communications.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="contact-name"
              >
                Name
              </Label>
              <Input
                id="contact-name"
                onChange={(e) => updateContact({ contactName: e.target.value })}
                placeholder="Full name"
                value={contactName}
              />
            </div>
            <div className="space-y-1.5">
              <Label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="contact-email"
              >
                Email
              </Label>
              <Input
                id="contact-email"
                onChange={(e) =>
                  updateContact({ contactEmail: e.target.value })
                }
                placeholder="email@company.com"
                type="email"
                value={contactEmail}
              />
            </div>
            <div className="space-y-1.5">
              <Label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="contact-phone"
              >
                Phone
              </Label>
              <PhoneInput
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                country="US"
                id="contact-phone"
                onChange={(value) =>
                  updateContact({ contactPhone: value || "" })
                }
                placeholder="(123) 456-7890"
                value={contactPhone}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* The user's own seat — added automatically, and easy to miss when it
          was just another row in the list. Its own card makes "you're
          registered by default" unmistakable, and removing it flips to an
          equally explicit "you're not attending" state. */}
      {selfAttendee && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                Your seat
                {selfIndex >= availableSeats ? (
                  <span className="text-sm font-normal text-amber-600 dark:text-amber-400">
                    Waitlisted
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-sm font-normal text-emerald-700 dark:text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    Reserved
                  </span>
                )}
              </CardTitle>
              <button
                className="text-xs text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                onClick={() => handleRemoveAttendee(selfIndex)}
                type="button"
              >
                Remove my seat
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Added automatically — remove it if you&apos;re registering others
              but not attending yourself.
            </p>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-gray-900 dark:text-white">
                {selfAttendee.firstName} {selfAttendee.lastName}
              </span>
              {selfAttendee.email && (
                <span className="text-xs text-muted-foreground truncate">
                  {selfAttendee.email}
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              <Label
                className="text-xs font-normal text-muted-foreground"
                htmlFor="special-requests-self"
              >
                Special requests
              </Label>
              <Textarea
                className="resize-none"
                id="special-requests-self"
                onChange={(e) =>
                  handleSpecialRequestChange(selfIndex, e.target.value)
                }
                placeholder="Dietary restrictions, accessibility needs, etc."
                rows={2}
                value={selfAttendee.specialRequests || ""}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {!selfAttendee && selfSeatWasOffered && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  You&apos;re not attending
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  You removed your own seat — you&apos;re registering others
                  only.
                </p>
              </div>
              <Button
                onClick={handleAddSelfBack}
                size="sm"
                type="button"
                variant="outline"
              >
                Add my seat back
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team card — search or type, hit Add. No edit/save step. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Bring your team
            <span className="text-sm font-normal text-muted-foreground ml-2">
              Optional
              {waitlistedCount > 0 ? ` · ${waitlistedCount} waitlisted` : ""}
            </span>
          </CardTitle>
          <CardDescription>
            Search your company directory or add attendees by hand — each one
            gets their own seat the moment you hit Add.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AddAttendees
            duplicateEmailError={duplicateEmailError}
            existingAttendees={eventInfo?.existingAttendees ?? []}
            onAddAttendee={handleAddAttendee}
            onClearDuplicateEmailError={() => setDuplicateEmailError(null)}
          />

          {attendees.some((a) => !a.isSelf) && (
            <div className="border-t border-border pt-1">
              {attendees.map((a, index) => {
                if (a.isSelf) return null;
                const requestValue = a.specialRequests || "";
                const isWaitlisted = index >= availableSeats;
                return (
                  <div
                    className="py-3 border-b border-border last:border-b-0"
                    key={`${a.email ?? "noemail"}-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {a.firstName} {a.lastName}
                      </span>
                      {a.email && (
                        <span className="text-xs text-muted-foreground">
                          {a.email}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          isWaitlisted
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        }`}
                      >
                        {isWaitlisted ? "Waitlist" : "Confirmed"}
                      </span>
                      <button
                        aria-label="Remove attendee"
                        className="ml-auto p-1 rounded text-muted-foreground/40 hover:text-destructive transition-colors flex-shrink-0"
                        onClick={() => handleRemoveAttendee(index)}
                        type="button"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      <Label
                        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                        htmlFor={`special-requests-${index}`}
                      >
                        Special requests
                      </Label>
                      <Textarea
                        className="resize-none"
                        id={`special-requests-${index}`}
                        onChange={(e) =>
                          handleSpecialRequestChange(index, e.target.value)
                        }
                        placeholder="Dietary restrictions, accessibility needs, etc."
                        rows={2}
                        value={requestValue}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group notes card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Group Notes
            <span className="text-sm font-normal text-muted-foreground ml-2">
              optional
            </span>
          </CardTitle>
          <CardDescription>
            Anything the organizer should know about your group.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            className="resize-none"
            onChange={(e) => updateContact({ groupNotes: e.target.value })}
            placeholder="e.g. We'll arrive 15 minutes early for setup"
            rows={3}
            value={groupNotes}
          />
        </CardContent>
      </Card>

      {/* Price summary + continue */}
      <div className="space-y-4">
        <PriceSummary
          attendeeCount={confirmedCount + waitlistedCount}
          includesSelf={selfSeatWasOffered ? Boolean(selfAttendee) : undefined}
          isCoveredByStage={isCoveredByStage}
          isCoveredByTotalAccess={isCoveredByTotalAccess}
          price={eventPrice}
          waitlistedCount={waitlistedCount}
        />
        <Button className="w-full" onClick={handleContinue} type="button">
          Continue to Payment
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
